import {
  SpellDefinition,
  EffectContext,
  SpellEffect,
  EffectParams,
  SpellState,
} from '../types/spell.types';
import { checkCondition } from '../effect-system/conditionChecker';
import { ActionDefinition, IActionTimings } from '../types/entity.types';
import { IPosition } from '../types/common.types';
import {
  DirectionEnum,
  DungeonEntityType,
  EffectShape,
  RangeAlgorithm,
} from '../types/enums';
import { chebyshevDistance, euclideanDistance } from '../utils/distance.utils';
import { gainMasteryXp } from './SpellMasteryService';
import { DungeonEntity } from '../entities/DungeonEntity';
import { SPELL_REGISTRY } from '../data/spells.config';
import { resolveBuffDef } from '../utils/buff.utils';
import { EFFECT_HANDLERS } from '../effect-system/effectHandlers';
import { msToTicks } from '../utils/timing.utils';
import {
  canReachThroughCorner,
  getDirectionFromPositions,
} from '../utils/map.utils';
import { resolveShapeCells } from '../utils/shape.utils';
import { mapService } from './MapService';
import { audioService } from './AudioService';
import { SfxId } from '../data/audio.config';

const SFX_EMOTIONS = ['joy', 'sadness', 'anger', 'fear', 'surprise', 'disgust'];

/** Per-emotion impact sound derived from a spell's first elemental effect. */
function spellSfxId(def: SpellDefinition): SfxId | undefined {
  for (const e of def.effects ?? []) {
    const el = (e.params as Record<string, unknown>).element;
    if (typeof el === 'string' && SFX_EMOTIONS.includes(el)) {
      return `spell-${el}` as SfxId;
    }
  }
  return undefined;
}
import { dungeonService } from './DungeonService';
import { projectileService } from './ProjectileService';

// --- Auto spell VFX (sprites/spells grid sheets, `spell-<num>-<row>`) ---
// Color row per emotion; row 5 (white) is the neutral fallback. cf.
// docs/vfx-audio.md "Auto-VFX des sorts".
const VFX_NEUTRAL_ROW = 5;
const VFX_EMOTION_ROW: Record<string, number> = {
  joy: 0,
  surprise: 1,
  sadness: 2,
  disgust: 3,
  anger: 7,
  fear: 8,
};
// Archetype sheets chosen by shape (symmetric effects favored — grid VFX do
// not rotate with direction).
const VFX_BURST = 3; // radial burst → AOE circles / cones / self auras
const VFX_SLASH = 124; // comma slash → melee single & swipe
const VFX_SPARK = 274; // 4-point sparkle → ranged single / line cells
// Global size tweak for spell VFX (renders 20% smaller than the source cell).
const SPELL_VFX_SCALE = 0.8;

// Handlers that deal the spell's damage — used to time the auto-VFX to the hit.
const DAMAGE_VFX_HANDLERS = new Set([
  'dealDamage',
  'dealDamageInCells',
  'dealFixedDamageInCells',
]);

/** Emotion of a spell, derived from its first elemental effect (else neutral). */
function spellEmotion(def: SpellDefinition): string | undefined {
  for (const e of def.effects ?? []) {
    const el = (e.params as Record<string, unknown>).element;
    if (typeof el === 'string' && SFX_EMOTIONS.includes(el)) {
      return el;
    }
  }
  return undefined;
}

/** `spell-<num>-<row>` grid animation name (files < 100 are zero-padded to 2). */
function vfxSheetName(sheet: number, row: number): string {
  const num = sheet < 100 ? String(sheet).padStart(2, '0') : String(sheet);
  return `spell-${num}-${row}`;
}

class SpellService {
  /**
   * Execute a spell from the registry.
   */
  execute(
    caster: DungeonEntity,
    spell: string | SpellDefinition,
    targetX?: number,
    targetY?: number,
    castSeq?: number,
  ): boolean {
    const def =
      typeof spell === 'string'
        ? caster.type === DungeonEntityType.PLAYER
          ? (caster.getSpellInstance(spell) ?? SPELL_REGISTRY[spell])
          : SPELL_REGISTRY[spell]
        : spell;
    if (!def) {
      return false;
    }
    const spellId = def.id;

    const state = caster.getSpellState(spellId);

    // Guards
    if (!caster.canCast()) {
      return false;
    }
    if (state.cooldownRemaining > 0) {
      return false;
    }

    // Cast condition checks
    if (def.castCondition && !checkCondition(caster, def.castCondition)) {
      return false;
    }

    const direction =
      targetX !== undefined && targetY !== undefined
        ? getDirectionFromPositions(
            caster.positionX,
            caster.positionY,
            targetX,
            targetY,
          )
        : caster.direction;

    // Self-targeted spell: ignore the passed target for cell resolution.
    // Otherwise shapes like CIRCLE would center on the adjacent cell passed
    // by player input rather than on the caster (case of analyst_psyWave).
    // The direction has already been derived above.
    if (def.parameter.type === 'self') {
      targetX = undefined;
      targetY = undefined;
    }

    // Minimum range: snipes are useless point-blank. Self spells reset target above, so they skip this.
    if (
      def.minRange !== undefined &&
      targetX !== undefined &&
      targetY !== undefined
    ) {
      const minRangeAlgo = def.rangeAlgorithm || RangeAlgorithm.CHEBYSHEV;
      const minRangeDist =
        minRangeAlgo === RangeAlgorithm.EUCLIDEAN
          ? euclideanDistance(
              caster.positionX,
              caster.positionY,
              targetX,
              targetY,
            )
          : chebyshevDistance(
              caster.positionX,
              caster.positionY,
              targetX,
              targetY,
            );
      if (minRangeDist < def.minRange) {
        return false;
      }
    }

    // Affected cells
    const affectedCells = this.resolveAffectedCells(
      caster,
      def,
      direction,
      targetX,
      targetY,
    );

    // Primary cell unreachable through a wall corner: the spell does NOT launch
    // at all — no animation, no effect — but the cooldown (and player mastery
    // XP) is still spent. The aim was wasted.
    const primaryReachable =
      targetX === undefined ||
      targetY === undefined ||
      canReachThroughCorner(
        caster.positionX,
        caster.positionY,
        targetX,
        targetY,
        {
          range: def.range,
          directionType: def.directionType,
          lineOfSight: def.lineOfSight,
          bypassDiagonalMiss: def.bypassDiagonalMiss,
        },
      );
    if (!primaryReachable) {
      this.applyCooldown(caster, def, state);
      if (caster.type === DungeonEntityType.PLAYER) {
        if (caster.id === dungeonService.localPlayerId) {
          gainMasteryXp(spellId, 1);
        }
      }
      return false;
    }

    // Context
    const ctx: EffectContext = {
      caster,
      targetX,
      targetY,
      direction,
      affectedCells,
      spell: def,
      spellState: state,
      castSeq,
    };

    // Preview tiles — skipped for projectile spells: the cell-in-front preview
    // is meaningless for a travelling projectile (no impact preview for now).
    let previewCreated = false;
    if (def.preview && !def.projectile) {
      this.createPreviewTiles(ctx, def);
      previewCreated = true;
    }

    // Orient caster
    caster.direction = direction;

    // Timings
    const timings = this.buildTimings(def);

    // Callbacks from effects
    const callbacks = this.buildCallbacks(ctx, def);

    // Delegate to existing action engine
    caster.startAction(def.id, true, timings, callbacks);

    const sfx = spellSfxId(def);
    if (sfx) {
      audioService.playSfx(sfx);
    }

    this.applyCooldown(caster, def, state);

    // Mastery XP gain — player only, per cast (so non-damaging spells level
    // up too). Placeholder granularity = 1 XP / cast; calibration Phase 11.
    if (caster.type === DungeonEntityType.PLAYER) {
      if (caster.id === dungeonService.localPlayerId) {
        gainMasteryXp(spellId, 1);
      }
    }

    return previewCreated;
  }

  /** Cooldown (config in ms → runtime ticks), honoring first-cast and
   * max-stacks skips. Spent both on a real cast and on a wasted (corner-blocked)
   * cast. */
  private applyCooldown(
    caster: DungeonEntity,
    def: SpellDefinition,
    state: SpellState,
  ): void {
    if (!def.cooldownMs) {
      return;
    }
    const firstCastSkip =
      caster.type === DungeonEntityType.PLAYER &&
      dungeonService.consumeFirstCastSkip();
    const maxStacksSkip =
      def.bypassCooldownAtMaxStacks !== undefined &&
      (() => {
        const buffId = def.bypassCooldownAtMaxStacks!;
        const stacks =
          caster.getActiveBuffs().find((b) => b.def.id === buffId)?.stacks ?? 0;
        const max = resolveBuffDef(caster, buffId)?.maxStacks ?? 1;
        return stacks >= max;
      })();
    if (!firstCastSkip && !maxStacksSkip) {
      state.cooldownRemaining = caster.computeCooldownTicks(
        def.id,
        def.cooldownMs,
      );
    }
  }

  /**
   * Convert a SpellDefinition into an ActionDefinition compatible with AIService.
   */
  toActionDefinition(
    caster: DungeonEntity,
    spellId: string,
  ): ActionDefinition | null {
    const def =
      caster.type === DungeonEntityType.PLAYER
        ? (caster.getSpellInstance(spellId) ?? SPELL_REGISTRY[spellId])
        : SPELL_REGISTRY[spellId];
    // Some player spell ids never resolve through the registry — e.g. the
    // synthetic `trinket-attack:<modelId>` id of a trinket-built basic attack, which
    // is cast via the SpellDefinition object directly. Return null so callers
    // (getAvailableActions) skip it instead of dereferencing undefined.
    if (!def) {
      return null;
    }
    return {
      name: def.id,
      parameter: def.parameter,
      execute: (x?: number, y?: number) => this.execute(caster, spellId, x, y),
      directionType: def.directionType,
      range: def.range,
      minRange: def.minRange,
      rangeAlgorithm: def.rangeAlgorithm,
      lineOfSight: def.lineOfSight,
      shapes: def.shapes,
      projectile: def.projectile,
      effects: def.effects,
      friendlyFire: def.friendlyFire,
      selfDamage: def.selfDamage,
      bypassDiagonalMiss: def.bypassDiagonalMiss,
    };
  }

  private resolveAffectedCells(
    caster: DungeonEntity,
    def: SpellDefinition,
    direction: DirectionEnum,
    targetX?: number,
    targetY?: number,
  ): IPosition[] {
    const cells = resolveShapeCells(def.shapes, {
      targetX,
      targetY,
      casterX: caster.positionX,
      casterY: caster.positionY,
      direction,
      defaultRange: def.range,
    });

    // Shapes that need entity / map lookups can't live in the pure
    // `resolveShapeCells` util — resolved here instead.
    for (const shape of def.shapes ?? []) {
      if (shape.type === EffectShape.ROOM) {
        const room = mapService.getRoomAt(caster.positionX, caster.positionY);
        if (room) {
          cells.push(...mapService.getRoomCells(room));
        }
      }
    }

    return cells;
  }

  private createPreviewTiles(ctx: EffectContext, def: SpellDefinition): void {
    if (!def.preview) {
      return;
    }
    // Phase timings are scaled by spellCastTime in DungeonEntity.startAction; the
    // preview must use the same multiplier or it expires before the (slower-casting)
    // damage lands.
    const multiplier = ctx.caster.getSpellCastTimeMult(def.id);
    const castTicks = msToTicks(def.timings.castMs) * multiplier;
    const actionTicks = msToTicks(def.timings.actionMs) * multiplier;
    const duration = castTicks + actionTicks;

    const groupId = ctx.caster.createAffectedTiles(ctx.affectedCells, {
      duration,
      type: def.preview.type,
      color: def.preview.color,
      alpha: def.preview.alpha,
      castSeq: ctx.castSeq,
    });
    ctx.caster.currentCastGroupId = groupId;
  }

  /** Client prediction: run ONLY a cast's blocking phases locally (cast/action/
   *  lag timings + LagType + phase sprites, NO effects), so the predicting
   *  client's `canMove`/`canCast` honor the lag while the host runs the real
   *  spell. Without it the local player ignores the lag, predicts a move the host
   *  rejects during it, and rubber-bands (origin → target). Mirrors the
   *  `startAction` in `execute`; the caller gates on `canCast` + cooldown. */
  predictCastBlocking(caster: DungeonEntity, def: SpellDefinition): void {
    caster.startAction(def.id, true, this.buildTimings(def), undefined);
  }

  // Client-only: predict the local player's telegraph for instant feedback, keyed
  // by castSeq so the host's preview-add/cast-rejected can reconcile it. Mirrors
  // execute's cell resolution; creates no effects.
  predictPreviewTiles(
    caster: DungeonEntity,
    spell: string | SpellDefinition,
    castSeq: number,
    targetX?: number,
    targetY?: number,
  ): void {
    const def =
      typeof spell === 'string'
        ? (caster.getSpellInstance(spell) ?? SPELL_REGISTRY[spell])
        : spell;
    if (!def || !def.preview || def.projectile) {
      return;
    }
    // Mirror execute's order exactly: derive direction from the ORIGINAL target
    // before nulling it for self-targeted spells, else a directional self spell
    // predicts the wrong cells (caster.direction instead of the aimed direction).
    const selfTargeted = def.parameter.type === 'self';
    const direction =
      targetX !== undefined && targetY !== undefined
        ? getDirectionFromPositions(
            caster.positionX,
            caster.positionY,
            targetX,
            targetY,
          )
        : caster.direction;
    const tx = selfTargeted ? undefined : targetX;
    const ty = selfTargeted ? undefined : targetY;
    const cells = this.resolveAffectedCells(caster, def, direction, tx, ty);
    const multiplier = caster.getSpellCastTimeMult(def.id);
    const duration =
      (msToTicks(def.timings.castMs) + msToTicks(def.timings.actionMs)) *
      multiplier;
    const groupId = caster.createAffectedTiles(cells, {
      duration,
      type: def.preview.type,
      color: def.preview.color,
      alpha: def.preview.alpha,
    });
    caster.currentCastGroupId = groupId;
    dungeonService.previewService.recordPredictedGroup(castSeq, groupId);
  }

  private buildTimings(def: SpellDefinition): IActionTimings {
    return {
      castTime: msToTicks(def.timings.castMs),
      castBlocking: def.timings.castBlocking,
      castSpriteName: def.animations.cast,
      actionTime: msToTicks(def.timings.actionMs),
      actionBlocking: def.timings.actionBlocking,
      actionSpriteName: def.animations.action,
      lagTime: msToTicks(def.timings.lagMs),
      lagBlocking: def.timings.lagBlocking,
      lagSpriteName: def.animations.lag,
    };
  }

  private buildCallbacks(ctx: EffectContext, def: SpellDefinition) {
    // Effects with timing.phase 'impact' are the projectile payload and are
    // intentionally excluded from every phase bucket (run by ProjectileService
    // on collision). cf. docs/spells.md.
    const castEffects = def.effects.filter(
      (e) => this.getPhaseInfo(e).phase === 'cast',
    );
    const actionEffects = def.effects.filter(
      (e) => this.getPhaseInfo(e).phase === 'action',
    );
    const lagEffects = def.effects.filter(
      (e) => this.getPhaseInfo(e).phase === 'lag',
    );
    const impactEffects = def.effects.filter(
      (e) => this.getPhaseInfo(e).phase === 'impact',
    );

    const actionCallbacks = this.toProgressCallbacks(ctx, actionEffects) ?? [];
    if (def.projectile) {
      actionCallbacks.push({
        percent: def.projectile.launchPercent ?? 0,
        callback: () => {
          projectileService.spawn({
            owner: ctx.caster,
            spell: def,
            direction: ctx.direction,
            impactEffects,
          });
        },
      });
    }

    const autoVfx = this.buildAutoVfxCallback(ctx, def);
    if (autoVfx) {
      actionCallbacks.push(autoVfx);
    }

    return {
      castProgressCallbacks: this.toProgressCallbacks(ctx, castEffects),
      actionProgressCallbacks: actionCallbacks.length
        ? actionCallbacks
        : undefined,
      lagProgressCallbacks: this.toProgressCallbacks(ctx, lagEffects),
    };
  }

  private getPhaseInfo(eff: SpellEffect): { phase: string; percent: number } {
    return {
      phase: eff.timing?.phase ?? 'action',
      percent: eff.timing?.percent ?? 0,
    };
  }

  /** Grid sheet for a spell with no explicit `vfx`, picked by primary shape. */
  private archetypeSheet(def: SpellDefinition): number {
    switch (def.shapes?.[0]?.type) {
      case EffectShape.CIRCLE:
      case EffectShape.CONE:
        return VFX_BURST;
      case EffectShape.SWIPE:
        return VFX_SLASH;
      case EffectShape.LINE:
        return VFX_SPARK;
      case EffectShape.SINGLE:
        return (def.range ?? 1) <= 1 ? VFX_SLASH : VFX_SPARK;
      default:
        return VFX_BURST; // self-cast buff → aura
    }
  }

  /**
   * Builds the spell-effect VFX callback played at the start of the action
   * phase: a grid animation derived from the spell's shape (placement + scale)
   * and emotion (color row). Skipped for projectiles (the bullet is the
   * visual) and for spells that already declare an explicit `playVfx` effect.
   */
  private buildAutoVfxCallback(ctx: EffectContext, def: SpellDefinition) {
    if (def.projectile) {
      return null;
    }
    if (
      def.effects?.some(
        (e) =>
          e.handler === 'playVfx' ||
          e.handler === 'dashLine' ||
          e.handler === 'hitscan',
      )
    ) {
      return null;
    }

    const emotion = spellEmotion(def);
    const row =
      def.vfx?.row ??
      (emotion ? VFX_EMOTION_ROW[emotion] : undefined) ??
      VFX_NEUTRAL_ROW;
    const sheet = def.vfx?.sheet ?? this.archetypeSheet(def);
    const name = vfxSheetName(sheet, row);
    const durationMs = def.timings.actionMs > 0 ? def.timings.actionMs : 300;

    const shape = def.shapes?.[0];
    const shapeType = shape?.type;
    const isSelf = def.parameter.type === 'self';
    const params: EffectParams = {
      name,
      durationMs,
      rotate: true,
      scale: SPELL_VFX_SCALE,
    };

    if (shapeType === EffectShape.CIRCLE) {
      // One enlarged VFX centered on the zone (diameter = 2·range + 1 tiles), +20%.
      params.scaleTiles = (2 * (shape?.range ?? 1) + 1) * 1.2;
      if (isSelf) {
        params.onCaster = true;
      }
    } else if (
      shapeType === EffectShape.LINE ||
      shapeType === EffectShape.CONE ||
      shapeType === EffectShape.SWIPE
    ) {
      params.onAffectedCells = true;
      if (shapeType === EffectShape.CONE) {
        params.scale = SPELL_VFX_SCALE * 1.4; // cones read larger
      }
    } else if (isSelf) {
      params.onCaster = true; // self buff aura
    }
    // SINGLE / default: plays on the target cell at default size.

    // Sync the VFX with the moment damage lands (start of the action phase felt
    // too early in playtests): fire at the first action damage effect's percent.
    const dmgEffect = def.effects.find(
      (e) =>
        this.getPhaseInfo(e).phase === 'action' &&
        DAMAGE_VFX_HANDLERS.has(e.handler),
    );

    return {
      percent: dmgEffect ? this.getPhaseInfo(dmgEffect).percent : 0,
      callback: () => EFFECT_HANDLERS.playVfx(ctx, params),
    };
  }

  private toProgressCallbacks(ctx: EffectContext, effects: SpellEffect[]) {
    if (effects.length === 0) {
      return undefined;
    }

    // Group by percent
    const byPercent = new Map<number, SpellEffect[]>();
    for (const eff of effects) {
      const { percent } = this.getPhaseInfo(eff);
      const group = byPercent.get(percent) ?? [];
      group.push(eff);
      byPercent.set(percent, group);
    }

    return Array.from(byPercent.entries()).map(([percent, effs]) => ({
      percent,
      callback: () => {
        for (const eff of effs) {
          const handler = EFFECT_HANDLERS[eff.handler];
          if (handler) {
            handler(ctx, eff.params);
          }
        }
      },
    }));
  }
}

export const spellExecutor = new SpellService();
